import { AxiosError } from 'axios'

export interface CooldownDetail {
  message: string
  next_resend: string
  expired_at: string
  code?: string | null
}

/** 429 cooldown = объект с next_resend. Rate-limit = строка от slowapi. */
export const parse429 = (error: unknown): CooldownDetail | null => {
  if (!(error instanceof AxiosError)) return null
  if (error.response?.status !== 429) return null
  const detail = (error.response.data as { detail?: unknown } | undefined)?.detail
  if (detail && typeof detail === 'object' && 'next_resend' in (detail as object)) {
    return detail as CooldownDetail
  }
  return null
}

export const getErrorDetail = (error: unknown): string | null => {
  if (!(error instanceof AxiosError)) return null
  const d = (error.response?.data as { detail?: unknown } | undefined)?.detail
  if (typeof d === 'string') return d
  if (d && typeof d === 'object' && 'message' in (d as object)) {
    const m = (d as { message?: unknown }).message
    return typeof m === 'string' ? m : null
  }
  return null
}

export const getStatus = (error: unknown): number | null => {
  if (!(error instanceof AxiosError)) return null
  return error.response?.status ?? null
}

/** Классификация бизнес-ошибок по статусу + сообщению из migration_guide. */
export type AuthErrorKind =
  | 'code_expired'
  | 'code_invalid'
  | 'code_not_found'
  | 'max_attempts'
  | 'already_authenticated'
  | 'rate_limit'
  | 'cooldown'
  | 'validation'
  | 'unauthorized'
  | 'user_already_has_email'
  | 'email_taken_by_other_tg'
  | 'user_already_has_telegram'
  | 'token_not_found'
  | 'token_expired'
  | 'token_already_used'
  | 'unknown'

export const classifyAuthError = (error: unknown): AuthErrorKind => {
  const status = getStatus(error)
  const detail = getErrorDetail(error) ?? ''
  if (parse429(error)) return 'cooldown'
  if (status === 429) return 'rate_limit'
  if (status === 422) return 'validation'
  if (status === 401) return 'unauthorized'
  if (status === 403 && /уже вошли/i.test(detail)) return 'already_authenticated'
  if (status === 400) {
    if (/не найден/i.test(detail)) return 'code_not_found'
    if (/истёк|истек/i.test(detail)) return 'code_expired'
    if (/неверный/i.test(detail)) return 'code_invalid'
    if (/ссылка истекла/i.test(detail)) return 'token_expired'
  }
  if (status === 404 && /ссылка недействительна/i.test(detail)) return 'token_not_found'
  if (status === 409) {
    if (/уже привязан email/i.test(detail)) return 'user_already_has_email'
    if (/email уже используется другим аккаунтом/i.test(detail)) return 'email_taken_by_other_tg'
    if (/уже связан с telegram|уже связан с другим telegram/i.test(detail)) return 'user_already_has_telegram'
    if (/уже использована/i.test(detail)) return 'token_already_used'
  }
  return 'unknown'
}
